// StageExamPage — fully proctored Q&A exam for pipeline stages with uploaded material.
//
// Proctoring:
//   - Identity photo on start (webcam snapshot)
//   - Fullscreen enforcement + violation on exit
//   - Tab/window switch detection (visibilitychange + blur)
//   - Copy/paste detection
//   - Countdown timer — auto-submits on expiry
//   - 3 high-severity violations → auto-submit
//   - All violations logged with timestamp and type
//
// Candidate NEVER sees score, answer key, or evaluation outcome.

import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "../../services/api";
import { useFaceDetector, useNoiseDetector } from "../common/ProctorGuard";
import {
  AlertTriangle, Camera, CheckCircle, Clock, Maximize2,
  Shield, ChevronRight, Loader2, EyeOff
} from "lucide-react";

const MAX_AUTO_SUBMIT_VIOLATIONS = 5; // auto-submit threshold

// Violation severity — high violations count toward auto-submit
const HIGH_SEVERITY = new Set(['tab_switch', 'copy_paste', 'multi_face', 'no_face']);

export default function StageExamPage({ jobId, candidateId, stageId, jobTitle, stageLabel, onComplete }) {
  const [phase, setPhase]       = useState("loading"); // loading|setup|exam|submitting|done|error
  const [examData, setExamData] = useState(null);
  const [answers, setAnswers]   = useState({});   // { [qId]: { selectedAnswer, selectedAnswers, writtenAnswer } }
  const [timeLeft, setTimeLeft] = useState(0);
  const [violations, setViolations] = useState([]);
  const [violationMsg, setViolationMsg] = useState("");
  const [violationModal, setViolationModal] = useState(null); // { type, description } — prominent overlay
  const [cameraGranted, setCameraGranted] = useState(false);
  const [cameraError, setCameraError]   = useState("");
  const [error, setError]       = useState("");
  const [autoSubmitReason, setAutoSubmitReason] = useState(null);
  const [faceWarning, setFaceWarning]   = useState("");  // current face warning message

  const videoRef      = useRef(null);
  const examVideoRef  = useRef(null);
  const streamRef     = useRef(null);
  const timerRef      = useRef(null);
  const startTimeRef  = useRef(null);
  const submittingRef = useRef(false);  // guard against double-submit

  // Load exam questions
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/jobs/${jobId}/stage/${stageId}/exam`);
        if (r.data?.success) {
          setExamData(r.data.data);
          setTimeLeft(r.data.data.timeLimit * 60);
          setPhase("setup");
        } else {
          setError(r.data?.message || "Could not load exam.");
          setPhase("error");
        }
      } catch (e) {
        setError(e.response?.data?.message || "Could not load exam.");
        setPhase("error");
      }
    })();
  }, [jobId, stageId]);

  // Capture a webcam snapshot (returns base64 JPEG or null)
  const captureSnapshot = useCallback(() => {
    const video = examVideoRef.current || videoRef.current;
    if (!video || video.readyState < 2) return null;
    try {
      const canvas = document.createElement('canvas');
      canvas.width  = 160;
      canvas.height = 120;
      canvas.getContext('2d').drawImage(video, 0, 0, 160, 120);
      return canvas.toDataURL('image/jpeg', 0.5); // ~4KB
    } catch { return null; }
  }, []);

  // Log a violation — attaches a snapshot when camera is active
  const logViolation = useCallback((type, description) => {
    console.log('[Proctoring] violation:', type, description);
    const snapshot  = captureSnapshot();
    const entry = { type, timestamp: new Date().toISOString(), description, ...(snapshot ? { screenshot: snapshot } : {}) };
    setViolations(v => {
      const next = [...v, entry];
      const highCount = next.filter(x => HIGH_SEVERITY.has(x.type)).length;
      setViolationMsg(`⚠ ${description}`);
      setViolationModal({ type, description });
      if (highCount >= MAX_AUTO_SUBMIT_VIOLATIONS) setAutoSubmitReason("violations");
      return next;
    });
  }, [captureSnapshot]);

  // ── Noise/speech detection during exam ──────────────────────────────────
  useNoiseDetector({
    active: phase === "exam",
    onNoise: useCallback((noiseType) => {
      const type = noiseType || 'background_noise';
      const msg  = type === 'background_speech'
        ? "Background speech or voices detected. Please ensure a quiet environment."
        : "Excessive background noise detected. Please ensure a quiet environment.";
      logViolation(type, msg);
    }, [logViolation]),
  });

  // ── Face detection (active during exam, uses examVideoRef) ──────────────
  useFaceDetector({
    active: phase === "exam",
    videoRef: examVideoRef,
    onNoFace: useCallback(() => {
      setFaceWarning("You are not visible on camera. Please stay in front of your webcam.");
      logViolation("no_face", "Candidate not visible on camera");
    }, [logViolation]),
    onMultiFace: useCallback((count) => {
      setFaceWarning(`${count} people detected on camera. Only the candidate should be visible.`);
      logViolation("multi_face", `Multiple people detected on camera (${count})`);
    }, [logViolation]),
  });

  // Proctoring event listeners (active only during exam)
  useEffect(() => {
    if (phase !== "exam") return;
    const onHide     = () => { if (document.hidden) logViolation("tab_switch", "Tab switched away from exam"); };
    const onBlur     = () => logViolation("focus_lost", "Exam window lost focus");
    const onCopy     = () => logViolation("copy_paste", "Copy detected during exam");
    const onPaste    = () => logViolation("copy_paste", "Paste detected during exam");
    const onFsChg    = () => { if (!document.fullscreenElement) logViolation("fullscreen_exit", "Exam exited fullscreen mode"); };
    const onCtxMenu  = (e) => { e.preventDefault(); logViolation("right_click", "Right-click attempted during exam"); };
    const onKeyDown  = (e) => {
      if ((e.ctrlKey || e.metaKey) && ['t','n','w','r'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        logViolation("keyboard_shortcut", `Keyboard shortcut blocked (${e.ctrlKey ? 'Ctrl' : 'Cmd'}+${e.key.toUpperCase()})`);
      }
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);
    document.addEventListener("copy",        onCopy);
    document.addEventListener("paste",       onPaste);
    document.addEventListener("fullscreenchange", onFsChg);
    document.addEventListener("contextmenu", onCtxMenu);
    document.addEventListener("keydown",     onKeyDown);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("copy",        onCopy);
      document.removeEventListener("paste",       onPaste);
      document.removeEventListener("fullscreenchange", onFsChg);
      document.removeEventListener("contextmenu", onCtxMenu);
      document.removeEventListener("keydown",     onKeyDown);
    };
  }, [phase, logViolation]);

  // Countdown timer
  useEffect(() => {
    if (phase !== "exam") return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setAutoSubmitReason("timeout"); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);

  // Auto-submit trigger
  useEffect(() => {
    if (autoSubmitReason) handleSubmit(autoSubmitReason);
  }, [autoSubmitReason]); // eslint-disable-line

  // Attach stream to exam mini-view when exam starts
  useEffect(() => {
    if (phase === "exam" && examVideoRef.current && streamRef.current) {
      examVideoRef.current.srcObject = streamRef.current;
    }
  }, [phase]);

  const requestCamera = async () => {
    setCameraError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      streamRef.current = stream;
      setCameraGranted(true);
      // videoRef may not exist yet — useEffect below attaches stream after render
    } catch {
      setCameraError("Camera access is required to begin the exam. Please allow camera access in your browser settings and try again.");
      // Do NOT set cameraGranted — hard block remains until camera is allowed
    }
  };

  // Attach stream to setup preview once video element is in the DOM
  useEffect(() => {
    if (cameraGranted && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraGranted]);

  const startExam = async () => {
    try { await document.documentElement.requestFullscreen(); } catch { /* user may deny */ }
    startTimeRef.current = Date.now();
    setPhase("exam");
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const handleSubmit = useCallback(async (reason = "manual") => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPhase("submitting");
    clearInterval(timerRef.current);
    stopCamera();
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});

    const timeTaken = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;

    // Build answers payload from state
    const answersPayload = Object.entries(answers).map(([questionId, ans]) => ({
      questionId,
      selectedAnswer:  ans.selectedAnswer  || null,
      selectedAnswers: ans.selectedAnswers || [],
      writtenAnswer:   ans.writtenAnswer   || "",
    }));

    try {
      // Capture final violations state via a ref to avoid stale closure
      const currentViolations = violations;
      const r = await api.post(`/public/jobs/${jobId}/stage/${stageId}/exam-submit`, {
        resumeId:   candidateId,
        answers:    answersPayload,
        violations: currentViolations,
        timeTaken,
      });
      if (r.data?.success) {
        setPhase("done");
        setTimeout(() => onComplete?.({ overallScore: null }), 3500);
      } else {
        setError(r.data?.message || "Submission failed.");
        setPhase("error");
      }
    } catch (e) {
      setError(e.response?.data?.message || "Submission failed.");
      setPhase("error");
    }
  }, [answers, violations, jobId, stageId, candidateId, onComplete]);

  const setAnswer = (qId, patch) => setAnswers(a => ({ ...a, [qId]: { ...(a[qId] || {}), ...patch } }));

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60).toString().padStart(2, "0");
    const s = (sec % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const answeredCount = Object.keys(answers).length;
  const totalQ        = examData?.questions?.length || 0;
  const timerCritical = timeLeft <= 120;

  // ── Loading ──────────────────────────────────────────────────────────────
  if (phase === "loading") return (
    <div className="flex items-center justify-center min-h-screen bg-[#1e2d5e]">
      <div className="text-center text-white">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-[#f07060]" />
        <p className="text-lg">Loading exam…</p>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────
  if (phase === "error") return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-2xl shadow-md max-w-md w-full mx-4 text-center">
        <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-800 mb-2">Exam unavailable</h2>
        <p className="text-gray-600 text-sm">{error}</p>
      </div>
    </div>
  );

  // ── Setup ────────────────────────────────────────────────────────────────
  if (phase === "setup") return (
    <div className="min-h-screen bg-[#1e2d5e] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8">
        <div className="text-center mb-6">
          <Shield className="w-12 h-12 text-[#1e2d5e] mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-[#1e2d5e]">{stageLabel}</h1>
          <p className="text-gray-500 text-sm mt-1">{jobTitle}</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5 text-sm">
          <p className="font-semibold text-blue-800 mb-2">Exam rules</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
            {/* First rule is always dynamic — question count + time limit */}
            <li>{totalQ} questions — {examData?.timeLimit} minutes</li>
            {/* Remaining rules: use HR-configured rules if available, else hardcoded defaults */}
            {(examData?.examRules?.length
              ? examData.examRules
              : [
                  'Exam runs in fullscreen — do not exit',
                  'Tab switching and copy/paste are monitored',
                  'Your webcam is required for identity verification',
                  'Exam auto-submits when time runs out',
                  'Scores and results are shared only with the hiring team',
                ]
            ).map((rule, i) => <li key={i}>{rule}</li>)}
          </ul>
          {/* Custom instructions from HR */}
          {examData?.candidateInstructions && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="font-semibold text-blue-800 mb-1">Additional instructions</p>
              <p className="text-blue-700 whitespace-pre-wrap text-sm">{examData.candidateInstructions}</p>
            </div>
          )}
        </div>

        {/* Camera setup */}
        <div className="mb-5">
          <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
            <Camera className="w-4 h-4" /> Identity verification
          </p>
          {!cameraGranted ? (
            <div>
              <button onClick={requestCamera}
                className="w-full py-2.5 bg-[#1e2d5e] text-white rounded-xl text-sm font-medium hover:bg-[#16234a] transition-colors">
                Allow camera access
              </button>
              {cameraError && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {cameraError}
                </p>
              )}
            </div>
          ) : (
            <div className="relative rounded-xl overflow-hidden bg-black h-32">
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
              <span className="absolute top-2 left-2 flex items-center gap-1 bg-green-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                ● Live
              </span>
            </div>
          )}
        </div>

        <button onClick={startExam} disabled={!cameraGranted}
          className="w-full py-3 bg-[#f07060] text-white rounded-xl text-sm font-semibold hover:bg-[#e06050] transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
          <Maximize2 className="w-4 h-4" />
          Begin Exam
          <ChevronRight className="w-4 h-4" />
        </button>
        {!cameraGranted && <p className="text-xs text-center text-gray-400 mt-2">Allow camera access to begin</p>}
      </div>
    </div>
  );

  // ── Submitting ───────────────────────────────────────────────────────────
  if (phase === "submitting") return (
    <div className="flex items-center justify-center min-h-screen bg-[#1e2d5e]">
      <div className="text-center text-white">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-[#f07060]" />
        <p className="text-lg font-semibold">Submitting your exam…</p>
        <p className="text-sm text-white/60 mt-1">Please wait</p>
      </div>
    </div>
  );

  // ── Done ─────────────────────────────────────────────────────────────────
  if (phase === "done") return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#1e2d5e] to-[#2a3f7a]">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-8 text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Exam Submitted</h2>
        <p className="text-gray-600 text-sm mb-4">
          Thank you for completing the assessment. Your responses have been recorded and will be reviewed by the hiring team.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-500">
          <EyeOff className="w-5 h-5 mx-auto mb-1 text-gray-400" />
          Results and scores are visible only to recruiters and are not shared with candidates.
        </div>
      </div>
    </div>
  );

  // ── Exam ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col select-none">
      {/* Proctoring violation overlay — shown for ALL violation types */}
      {(faceWarning || violationModal) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 text-center border-4 border-orange-400">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
            <h2 className="text-xl font-extrabold mb-2 text-orange-700">Proctoring Violation Detected</h2>
            <p className="text-gray-700 text-sm mb-3 leading-relaxed">
              {faceWarning || violationModal?.description}
            </p>
            <div className="bg-orange-50 rounded-lg px-4 py-2 mb-5 text-xs text-orange-700 font-medium">
              Total violations recorded: <strong>{violations.length}</strong> &nbsp;·&nbsp; {MAX_AUTO_SUBMIT_VIOLATIONS - violations.filter(x => HIGH_SEVERITY.has(x.type)).length > 0 ? `${MAX_AUTO_SUBMIT_VIOLATIONS - violations.filter(x => HIGH_SEVERITY.has(x.type)).length} high-severity allowed before auto-submit` : 'Auto-submitting…'}
            </div>
            <button
              onClick={() => { setFaceWarning(""); setViolationModal(null); }}
              className="w-full py-3 rounded-xl font-bold text-white bg-orange-500 hover:bg-orange-600 transition-colors"
            >
              I Understand — Return to Exam
            </button>
          </div>
        </div>
      )}
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-[#1e2d5e] text-white px-6 py-3 flex items-center justify-between shadow-lg">
        <div>
          <p className="font-bold text-sm">{stageLabel}</p>
          <p className="text-xs text-white/60">{jobTitle}</p>
        </div>
        <div className="flex items-center gap-4">
          {violations.length > 0 && (
            <span className="flex items-center gap-1 text-xs font-semibold text-red-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {violations.length} violation{violations.length > 1 ? 's' : ''}
            </span>
          )}
          <span className="text-xs text-white/70">{answeredCount}/{totalQ} answered</span>
          <span className={`flex items-center gap-1.5 font-mono font-bold text-sm px-3 py-1 rounded-full ${
            timerCritical ? "bg-red-500 animate-pulse" : "bg-white/10"
          }`}>
            <Clock className="w-3.5 h-3.5" />
            {formatTime(timeLeft)}
          </span>
          {cameraGranted && streamRef.current && (
            <video ref={examVideoRef} autoPlay muted playsInline
              className="w-16 h-12 rounded-lg object-cover border-2 border-white/20" />
          )}
        </div>
      </div>

      {/* Violation banner */}
      {violationMsg && (
        <div className="bg-red-50 border-b border-red-200 px-6 py-2 flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {violationMsg}
          <button onClick={() => setViolationMsg("")} className="ml-auto text-xs text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Questions */}
      <div className="flex-1 max-w-3xl mx-auto w-full p-6 space-y-6">
        {(examData?.questions || []).map((q, idx) => {
          const ans     = answers[q._id] || {};
          const qType   = q.questionType || "mcq";
          const answered = qType === "multi_select"
            ? (ans.selectedAnswers?.length > 0)
            : qType === "short_answer" || qType === "long_answer" || qType === "scenario" || qType === "case_study"
            ? !!ans.writtenAnswer?.trim()
            : !!ans.selectedAnswer;

          return (
            <div key={q._id} className={`bg-white rounded-xl border-2 p-5 shadow-sm transition-colors ${answered ? "border-[#1e2d5e]" : "border-gray-200"}`}>
              {/* Question header */}
              <div className="flex items-start gap-3 mb-4">
                <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${answered ? "bg-[#1e2d5e] text-white" : "bg-gray-100 text-gray-500"}`}>
                  {idx + 1}
                </span>
                <div className="flex-1">
                  <span className="text-[10px] font-semibold uppercase text-gray-400 mr-2">{q.difficulty}</span>
                  <span className="text-[10px] font-semibold uppercase text-orange-400">{q.points}pts</span>
                  <p className="text-gray-800 font-medium leading-relaxed mt-1 whitespace-pre-wrap">{q.question}</p>
                </div>
              </div>

              {/* Answer input by type */}
              <div className="ml-10">
                {/* MCQ / Single Select / True-False */}
                {["mcq","single_select","true_false"].includes(qType) && (
                  <div className="space-y-2">
                    {(q.options || []).map((opt, oi) => (
                      <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                        ans.selectedAnswer === opt ? "border-[#1e2d5e] bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}>
                        <input type="radio" name={`q-${q._id}`} value={opt}
                          checked={ans.selectedAnswer === opt}
                          onChange={() => setAnswer(q._id, { selectedAnswer: opt })}
                          className="accent-[#1e2d5e]" />
                        <span className="text-sm text-gray-700">{opt}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Multi-Select */}
                {qType === "multi_select" && (
                  <div className="space-y-2">
                    <p className="text-xs text-gray-400 mb-1">Select all that apply</p>
                    {(q.options || []).map((opt, oi) => {
                      const selected = (ans.selectedAnswers || []).includes(opt);
                      return (
                        <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-colors ${
                          selected ? "border-[#1e2d5e] bg-blue-50" : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                        }`}>
                          <input type="checkbox" checked={selected}
                            onChange={() => {
                              const cur = ans.selectedAnswers || [];
                              setAnswer(q._id, { selectedAnswers: selected ? cur.filter(x => x !== opt) : [...cur, opt] });
                            }}
                            className="accent-[#1e2d5e]" />
                          <span className="text-sm text-gray-700">{opt}</span>
                        </label>
                      );
                    })}
                  </div>
                )}

                {/* Short Answer */}
                {qType === "short_answer" && (
                  <textarea rows={3} placeholder="Write your answer here…"
                    value={ans.writtenAnswer || ""}
                    onChange={e => setAnswer(q._id, { writtenAnswer: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e2d5e]/30 resize-none" />
                )}

                {/* Long Answer / Scenario / Case Study */}
                {["long_answer","scenario","case_study"].includes(qType) && (
                  <textarea rows={6} placeholder="Write your detailed answer here…"
                    value={ans.writtenAnswer || ""}
                    onChange={e => setAnswer(q._id, { writtenAnswer: e.target.value })}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e2d5e]/30 resize-none" />
                )}
              </div>
            </div>
          );
        })}

        {/* Submit */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 text-center shadow-sm">
          <p className="text-sm text-gray-500 mb-3">
            {answeredCount < totalQ
              ? `${totalQ - answeredCount} question(s) unanswered — you can still submit`
              : "All questions answered — ready to submit!"}
          </p>
          <button onClick={() => handleSubmit("manual")}
            className="px-8 py-3 bg-[#f07060] text-white rounded-xl font-semibold hover:bg-[#e06050] transition-colors">
            Submit Exam
          </button>
        </div>
      </div>
    </div>
  );
}
